import React, { useState, createElement } from 'react';
import { SearchIcon, FilterIcon, XIcon, ChevronRightIcon, ArrowUpIcon, ArrowDownIcon, DownloadIcon, UserIcon, ClockIcon, InfoIcon } from 'lucide-react';
import { AuditRecord, AuditActionType, AuditModuleType } from './AuditManagement';
import { StatusBadge } from '../../StatusBadge';
type SortField = 'timestamp' | 'user' | 'action' | 'module' | 'object';
type SortDirection = 'asc' | 'desc';
interface AuditTableProps {
  auditRecords: AuditRecord[];
  onViewAudit: (audit: AuditRecord) => void;
  locale: string;
}
export function AuditTable({
  auditRecords,
  onViewAudit,
  locale
}: AuditTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    module: '',
    action: '',
    user: '',
    entity: '',
    site: '',
    dateFrom: '',
    dateTo: '',
    field: ''
  });
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const resetFilters = () => {
    setFilters({
      module: '',
      action: '',
      user: '',
      entity: '',
      site: '',
      dateFrom: '',
      dateTo: '',
      field: ''
    });
    setSearchTerm('');
  };
  // Get unique values for filter dropdowns
  const uniqueModules = Array.from(new Set(auditRecords.map(record => record.module)));
  const uniqueActions = Array.from(new Set(auditRecords.map(record => record.action)));
  const uniqueUsers = Array.from(new Set(auditRecords.map(record => record.userName)));
  const uniqueEntities = Array.from(new Set(auditRecords.filter(record => record.entityName).map(record => record.entityName)));
  const uniqueSites = Array.from(new Set(auditRecords.filter(record => record.siteName).map(record => record.siteName)));
  // Get all field names from changes for field filter
  const allFields = new Set<string>();
  auditRecords.forEach(record => {
    record.changes.forEach(change => {
      allFields.add(change.field);
    });
  });
  const uniqueFields = Array.from(allFields);
  // Apply filters and search
  const filteredRecords = auditRecords.filter(record => {
    // Search term filter (search in objectId, objectName, and field changes)
    const searchMatch = searchTerm === '' || record.objectId.toLowerCase().includes(searchTerm.toLowerCase()) || record.objectName.toLowerCase().includes(searchTerm.toLowerCase()) || record.changes.some(change => change.field.toLowerCase().includes(searchTerm.toLowerCase()) || change.oldValue && change.oldValue.toLowerCase().includes(searchTerm.toLowerCase()) || change.newValue && change.newValue.toLowerCase().includes(searchTerm.toLowerCase()));
    // Module filter
    const moduleMatch = !filters.module || record.module === filters.module;
    // Action filter
    const actionMatch = !filters.action || record.action === filters.action;
    // User filter
    const userMatch = !filters.user || record.userName === filters.user;
    // Entity filter
    const entityMatch = !filters.entity || record.entityName === filters.entity;
    // Site filter
    const siteMatch = !filters.site || record.siteName === filters.site;
    // Field filter
    const fieldMatch = !filters.field || record.changes.some(change => change.field === filters.field);
    // Date range filter
    const recordDate = new Date(record.timestamp);
    const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
    const dateMatch = (!fromDate || recordDate >= fromDate) && (!toDate || recordDate <= toDate);
    return searchMatch && moduleMatch && actionMatch && userMatch && entityMatch && siteMatch && fieldMatch && dateMatch;
  });
  // Apply sorting
  const sortedRecords = [...filteredRecords].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'timestamp':
        comparison = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        break;
      case 'user':
        comparison = a.userName.localeCompare(b.userName);
        break;
      case 'action':
        comparison = a.action.localeCompare(b.action);
        break;
      case 'module':
        comparison = a.module.localeCompare(b.module);
        break;
      case 'object':
        const objectCompare = a.objectName.localeCompare(b.objectName);
        if (objectCompare !== 0) {
          comparison = objectCompare;
        } else {
          comparison = a.objectId.localeCompare(b.objectId);
        }
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Function to export audit logs to CSV
  const exportToCSV = () => {
    // Header row
    let csvContent = 'Timestamp,User,User Type,Action,Module,Object ID,Object Name,IP Address,Entity,Site,Changed Fields\n';
    // Data rows
    sortedRecords.forEach(record => {
      const changedFields = record.changes.map(change => `${change.field}: ${change.oldValue || ''} → ${change.newValue || ''}`).join('; ');
      const row = [new Date(record.timestamp).toLocaleString(locale), record.userName, record.userType, record.action, record.module, record.objectId, `"${record.objectName.replace(/"/g, '""')}"`, record.ipAddress || '', record.entityName || '', record.siteName || '', `"${changedFields.replace(/"/g, '""')}"` // Escape quotes in CSV
      ];
      csvContent += row.join(',') + '\n';
    });
    // Create and download the file
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_log_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Render action badge
  const renderActionBadge = (action: AuditActionType) => {
    switch (action) {
      case 'Created':
        return <StatusBadge status="success" text="Created" data-id="element-14452" />;
      case 'Updated':
        return <StatusBadge status="warning" text="Updated" data-id="element-14453" />;
      case 'Deleted':
        return <StatusBadge status="error" text="Deleted" data-id="element-14454" />;
      case 'Approved':
        return <StatusBadge status="success" text="Approved" data-id="element-14455" />;
      case 'Rejected':
        return <StatusBadge status="error" text="Rejected" data-id="element-14456" />;
      case 'Viewed':
        return <StatusBadge status="default" text="Viewed" data-id="element-14457" />;
      case 'Logged In':
        return <StatusBadge status="info" text="Logged In" data-id="element-14458" />;
      case 'Logged Out':
        return <StatusBadge status="default" text="Logged Out" data-id="element-14459" />;
      case 'Password Changed':
        return <StatusBadge status="warning" text="Password Changed" data-id="element-14460" />;
      case 'Permission Changed':
        return <StatusBadge status="warning" text="Permission Changed" data-id="element-14461" />;
      case 'Configuration Changed':
        return <StatusBadge status="warning" text="Config Changed" data-id="element-14462" />;
      case 'License Activated':
        return <StatusBadge status="success" text="License Activated" data-id="element-14463" />;
      case 'License Deactivated':
        return <StatusBadge status="error" text="License Deactivated" data-id="element-14464" />;
      case 'Pass Used':
        return <StatusBadge status="info" text="Pass Used" data-id="element-14465" />;
      default:
        return <StatusBadge status="default" text={action} data-id="element-14466" />;
    }
  };
  // Get summary of changes for display
  const getChangeSummary = (changes: {
    field: string;
    oldValue: string | null;
    newValue: string | null;
  }[]) => {
    if (changes.length === 0) {
      return 'No changes recorded';
    }
    if (changes.length === 1) {
      const change = changes[0];
      return `${change.field}: ${change.oldValue || 'null'} → ${change.newValue || 'null'}`;
    }
    return `${changes.length} fields changed: ${changes.map(c => c.field).join(', ')}`;
  };
  return <div className="space-y-6" data-id="element-14467">
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-14468">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-14469">
          <div className="flex-1 max-w-md" data-id="element-14470">
            <div className="relative rounded-md shadow-sm" data-id="element-14471">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-14472">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-14473" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search objects, IDs, or field changes..." data-id="element-14474" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-14475">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14476">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-14477" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-14478">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            <button onClick={exportToCSV} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14479">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-14480" />
              Export CSV
            </button>
          </div>
        </div>
        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-14481">
            {filters.module && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-14482">
                Module: {filters.module}
                <button type="button" onClick={() => setFilters({
            ...filters,
            module: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-14483">
                  <XIcon className="h-3 w-3" data-id="element-14484" />
                </button>
              </span>}
            {filters.action && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800" data-id="element-14485">
                Action: {filters.action}
                <button type="button" onClick={() => setFilters({
            ...filters,
            action: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none" data-id="element-14486">
                  <XIcon className="h-3 w-3" data-id="element-14487" />
                </button>
              </span>}
            {filters.user && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-14488">
                User: {filters.user}
                <button type="button" onClick={() => setFilters({
            ...filters,
            user: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-14489">
                  <XIcon className="h-3 w-3" data-id="element-14490" />
                </button>
              </span>}
            {filters.entity && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-800" data-id="element-14491">
                Entity: {filters.entity}
                <button type="button" onClick={() => setFilters({
            ...filters,
            entity: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-red-400 hover:bg-red-200 hover:text-red-500 focus:outline-none" data-id="element-14492">
                  <XIcon className="h-3 w-3" data-id="element-14493" />
                </button>
              </span>}
            {filters.site && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800" data-id="element-14494">
                Site: {filters.site}
                <button type="button" onClick={() => setFilters({
            ...filters,
            site: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500 focus:outline-none" data-id="element-14495">
                  <XIcon className="h-3 w-3" data-id="element-14496" />
                </button>
              </span>}
            {filters.field && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800" data-id="element-14497">
                Field: {filters.field}
                <button type="button" onClick={() => setFilters({
            ...filters,
            field: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-yellow-400 hover:bg-yellow-200 hover:text-yellow-500 focus:outline-none" data-id="element-14498">
                  <XIcon className="h-3 w-3" data-id="element-14499" />
                </button>
              </span>}
            {(filters.dateFrom || filters.dateTo) && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-14500">
                Date:{' '}
                {filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString(locale) : 'Any'}
                {filters.dateTo ? ` to ${new Date(filters.dateTo).toLocaleDateString(locale)}` : ''}
                <button type="button" onClick={() => setFilters({
            ...filters,
            dateFrom: '',
            dateTo: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-500 focus:outline-none" data-id="element-14501">
                  <XIcon className="h-3 w-3" data-id="element-14502" />
                </button>
              </span>}
            <button type="button" onClick={resetFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14503">
              Clear All
            </button>
          </div>}
        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-3" data-id="element-14504">
            <div data-id="element-14505">
              <label htmlFor="module" className="block text-sm font-medium text-gray-700" data-id="element-14506">
                Module / Area
              </label>
              <select id="module" name="module" value={filters.module} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-14507">
                <option value="" data-id="element-14508">All Modules</option>
                {uniqueModules.map(module => <option key={module} value={module} data-id="element-14509">
                    {module}
                  </option>)}
              </select>
            </div>
            <div data-id="element-14510">
              <label htmlFor="action" className="block text-sm font-medium text-gray-700" data-id="element-14511">
                Action Type
              </label>
              <select id="action" name="action" value={filters.action} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-14512">
                <option value="" data-id="element-14513">All Actions</option>
                {uniqueActions.map(action => <option key={action} value={action} data-id="element-14514">
                    {action}
                  </option>)}
              </select>
            </div>
            <div data-id="element-14515">
              <label htmlFor="user" className="block text-sm font-medium text-gray-700" data-id="element-14516">
                User
              </label>
              <select id="user" name="user" value={filters.user} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-14517">
                <option value="" data-id="element-14518">All Users</option>
                {uniqueUsers.map(user => <option key={user} value={user} data-id="element-14519">
                    {user}
                  </option>)}
              </select>
            </div>
            <div data-id="element-14520">
              <label htmlFor="entity" className="block text-sm font-medium text-gray-700" data-id="element-14521">
                Entity
              </label>
              <select id="entity" name="entity" value={filters.entity} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-14522">
                <option value="" data-id="element-14523">All Entities</option>
                {uniqueEntities.map(entity => <option key={entity} value={entity} data-id="element-14524">
                    {entity}
                  </option>)}
              </select>
            </div>
            <div data-id="element-14525">
              <label htmlFor="site" className="block text-sm font-medium text-gray-700" data-id="element-14526">
                Site
              </label>
              <select id="site" name="site" value={filters.site} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-14527">
                <option value="" data-id="element-14528">All Sites</option>
                {uniqueSites.map(site => <option key={site} value={site} data-id="element-14529">
                    {site}
                  </option>)}
              </select>
            </div>
            <div data-id="element-14530">
              <label htmlFor="field" className="block text-sm font-medium text-gray-700" data-id="element-14531">
                Changed Field
              </label>
              <select id="field" name="field" value={filters.field} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-14532">
                <option value="" data-id="element-14533">All Fields</option>
                {uniqueFields.map(field => <option key={field} value={field} data-id="element-14534">
                    {field}
                  </option>)}
              </select>
            </div>
            <div data-id="element-14535">
              <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700" data-id="element-14536">
                From Date
              </label>
              <input type="date" id="dateFrom" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-14537" />
            </div>
            <div data-id="element-14538">
              <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700" data-id="element-14539">
                To Date
              </label>
              <input type="date" id="dateTo" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-14540" />
            </div>
          </div>}
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-14541">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-14542">
          <thead className="bg-gray-50" data-id="element-14543">
            <tr data-id="element-14544">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('timestamp')} data-id="element-14545">
                <div className="flex items-center space-x-1" data-id="element-14546">
                  <span data-id="element-14547">Timestamp</span>
                  {sortField === 'timestamp' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-14548" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-14549" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('user')} data-id="element-14550">
                <div className="flex items-center space-x-1" data-id="element-14551">
                  <span data-id="element-14552">User</span>
                  {sortField === 'user' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-14553" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-14554" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('action')} data-id="element-14555">
                <div className="flex items-center space-x-1" data-id="element-14556">
                  <span data-id="element-14557">Action</span>
                  {sortField === 'action' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-14558" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-14559" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('module')} data-id="element-14560">
                <div className="flex items-center space-x-1" data-id="element-14561">
                  <span data-id="element-14562">Module</span>
                  {sortField === 'module' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-14563" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-14564" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('object')} data-id="element-14565">
                <div className="flex items-center space-x-1" data-id="element-14566">
                  <span data-id="element-14567">Object</span>
                  {sortField === 'object' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-14568" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-14569" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14570">
                Changes
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14571">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-14572">
            {sortedRecords.length === 0 ? <tr data-id="element-14573">
                <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-14574">
                  No audit records found matching the current filters.
                </td>
              </tr> : sortedRecords.map(record => <tr key={record.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onViewAudit(record)} data-id="element-14575">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14576">
                    <div className="flex items-center" data-id="element-14577">
                      <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-14578" />
                      {formatTimestamp(record.timestamp)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-14579">
                    <div className="flex items-center" data-id="element-14580">
                      <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-14581" />
                      <div data-id="element-14582">
                        <div className="text-sm font-medium text-gray-900" data-id="element-14583">
                          {record.userName}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-14584">
                          {record.userType}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-14585">
                    {renderActionBadge(record.action)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14586">
                    {record.module}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-14587">
                    <div className="text-sm font-medium text-indigo-600" data-id="element-14588">
                      {record.objectId}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-14589">
                      {record.objectName}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" data-id="element-14590">
                    <div className="max-w-xs" data-id="element-14591">
                      <p className="truncate" data-id="element-14592">
                        {getChangeSummary(record.changes)}
                      </p>
                      {record.entityName && <p className="text-xs text-gray-400 mt-1" data-id="element-14593">
                          {record.entityName}
                          {record.siteName ? ` / ${record.siteName}` : ''}
                        </p>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-14594">
                    <div className="flex justify-end items-center" data-id="element-14595">
                      <InfoIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-14596" />
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-14597" />
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}